export interface HistoryOpBase {
  /** Approximate heap cost of the op, used for the eviction budget. */
  bytes: number;
}

export interface HistoryStep<TOp extends HistoryOpBase> {
  label: string;
  ops: TOp[];
  bytes: number;
}

export interface HistoryStackOptions {
  /** Total budget for retained snapshots. Oldest steps are dropped first. */
  maxBytes?: number;
  maxSteps?: number;
}

const DEFAULT_MAX_BYTES = 1024 * 1024 * 1024;
const DEFAULT_MAX_STEPS = 50;

/**
 * Linear undo stack. Steps are opaque bags of ops — applying and reverting them
 * is the caller's job, which keeps this module free of GL and DOM dependencies.
 *
 * `position` is the index of the newest applied step; -1 means "nothing from the
 * stack is applied", i.e. the oldest retained state.
 */
export class HistoryStack<TOp extends HistoryOpBase> {
  private readonly maxBytes: number;
  private readonly maxSteps: number;
  private items: HistoryStep<TOp>[] = [];
  private cursor = -1;
  private totalBytes = 0;

  constructor(options: HistoryStackOptions = {}) {
    this.maxBytes = options.maxBytes ?? DEFAULT_MAX_BYTES;
    this.maxSteps = options.maxSteps ?? DEFAULT_MAX_STEPS;
  }

  get steps(): readonly HistoryStep<TOp>[] {
    return this.items;
  }

  get position(): number {
    return this.cursor;
  }

  get bytes(): number {
    return this.totalBytes;
  }

  get canUndo(): boolean {
    return this.cursor >= 0;
  }

  get canRedo(): boolean {
    return this.cursor < this.items.length - 1;
  }

  push(label: string, ops: TOp[]): void {
    if (ops.length === 0) return;

    // Anything past the cursor is unreachable once a new step is recorded.
    if (this.canRedo) {
      const dropped = this.items.splice(this.cursor + 1);
      for (const step of dropped) this.totalBytes -= step.bytes;
    }

    const bytes = ops.reduce((sum, op) => sum + op.bytes, 0);
    this.items.push({ label, ops, bytes });
    this.totalBytes += bytes;
    this.cursor = this.items.length - 1;
    this.evict();
  }

  undo(): HistoryStep<TOp> | null {
    if (!this.canUndo) return null;
    const step = this.items[this.cursor];
    this.cursor -= 1;
    return step;
  }

  redo(): HistoryStep<TOp> | null {
    if (!this.canRedo) return null;
    this.cursor += 1;
    return this.items[this.cursor];
  }

  clear(): void {
    this.items = [];
    this.cursor = -1;
    this.totalBytes = 0;
  }

  /**
   * Drops the oldest steps until the budget is met. A step that is still
   * reachable by undo is never dropped unless it is the oldest one, so the
   * effect is simply that history stops further back than the user might like.
   */
  private evict(): void {
    while (
      this.items.length > 0 &&
      (this.totalBytes > this.maxBytes || this.items.length > this.maxSteps)
    ) {
      const [dropped] = this.items.splice(0, 1);
      this.totalBytes -= dropped.bytes;
      this.cursor -= 1;
      if (this.cursor < -1) this.cursor = -1;
    }
  }
}
